/**
 * Utility functions for serializing comments to different formats
 */
import { formatTimecode, formatDatetime } from './shared-utils';

/**
 * Generate an EDL file from comments
 * @param {Array} comments - Array of comment objects
 * @param {Object} videoFile - Video file object
 * @returns {Object} - Object with content and filename
 */
export const generateEDL = (comments, videoFile) => {
  if (!comments || comments.length === 0) {
    throw new Error('No comments to export');
  }
  
  try {
    // Get video file name for the EDL title
    const edlTitle = cleanFilename(videoFile);
    
    // EDL header
    let edlContent = `TITLE: ${edlTitle} Comments\nFCM: NON-DROP FRAME\n\n`;
    
    const colors = ['ResolveColorYellow', 'ResolveColorGreen', 'ResolveColorBlue', 'ResolveColorPurple', 'ResolveColorPink', 'ResolveColorRed'];
    
    // Create a map of user_id to color index
    const userColorMap = {};
    let nextColorIndex = 0;

    // Sort comments by timestamp
    const sortedComments = [...comments]
      .filter(c => !c.parent_id)
      .sort((a, b) => a.timestamp - b.timestamp);
      
    // First pass to assign colors to each unique user_id
    sortedComments.forEach(comment => {
      if (!(comment.user_id in userColorMap)) {
        userColorMap[comment.user_id] = nextColorIndex % colors.length;
        nextColorIndex++;
      }
    });
    
    // Process comments to create EDL entries
    sortedComments.forEach((comment, index) => {
      // EDL requires timecode in HH:MM:SS:FF format
      const timecodeStr = formatTimecode(comment.timestamp, videoFile.file_framerate);
      
      // Calculate a small duration (e.g., 2 seconds) for each marker
      const endTimestamp = comment.timestamp + 0.1;
      const endTimecodeStr = formatTimecode(endTimestamp, videoFile.file_framerate);
      
      // Create EDL entry (standard CMX3600 format)
      // Format: EditNum Reel EditType TimelineIn TimelineOut SourceIn SourceOut
      const editNum = (index + 1).toString().padStart(3, '0');
      edlContent += `${editNum}  001       V     C        ${timecodeStr} ${endTimecodeStr} ${timecodeStr} ${endTimecodeStr}\n`;
      edlContent += `* COMMENT: [${comment.user_first_name} ${comment.user_last_name}] ${sanitizeForEDL(comment.text)}\n`;

      const sortedReplies = [...comments]
        .filter(c => c.parent_id === comment.id)
        .sort((a, b) => a.timestamp - b.timestamp);
      sortedReplies.forEach((reply) => {
        edlContent += `* REPLY: [${reply.user_first_name} ${reply.user_last_name}] ${sanitizeForEDL(reply.text)}\n`;
      });

      const colorIndex = userColorMap[comment.user_id];
      edlContent += `|C:${colors[colorIndex]} |M:Acute Pro Note [${comment.user_first_name} ${comment.user_last_name}] |D:1\n\n`;
    });
    
    return {
      content: edlContent,
      filename: `${edlTitle}.edl`
    };
  } catch (err) {
    console.error('Error generating EDL file:', err);
    throw new Error('Failed to generate EDL file');
  }
};

/**
 * Sanitize text for EDL export
 * @param {String} text - Text to sanitize
 * @returns {String} - Sanitized text
 */
export const sanitizeForEDL = (text) => {
  return text.replace(/\n/g, ' ').replace(/\|/g, '');
};

/**
 * Generate a CSV file from comments
 * @param {Array} comments - Array of comment objects
 * @param {Object} videoFile - Video file object
 * @returns {Object} - Object with content and filename
 */
export const generateCSV = (comments, videoFile) => {
  if (!comments || comments.length === 0) {
    throw new Error('No comments to export');
  }
  
  try {
    // CSV header
    let csvContent = 'Timestamp,Timecode,Author,Date,Comment\n';
    
    // Sort comments by timestamp
    const sortedComments = [...comments]
      .filter(c => !c.parent_id)
      .sort((a, b) => a.timestamp - b.timestamp);
    
    // Process comments to create CSV entries
    sortedComments.forEach(comment => {
      // Add main comment
      const timestamp = comment.timestamp || 0;
      const timecode = formatTimecode(timestamp, videoFile.file_framerate);
      const author = `${comment.user_first_name} ${comment.user_last_name}`;
      const date = formatDatetime(comment.created_at);
      const text = sanitizeForCSV(comment.text);
      
      csvContent += `${timestamp},"${timecode}","${author}","${date}","${text}"\n`;
      
      // Add replies if any
      const sortedReplies = [...comments]
        .filter(c => c.parent_id === comment.id)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      sortedReplies.forEach(reply => {
        const replyAuthor = `${reply.user_first_name} ${reply.user_last_name}`;
        const replyDate = formatDatetime(reply.created_at);
        const replyText = `[REPLY] ${sanitizeForCSV(reply.text)}`;
        
        csvContent += `${timestamp},"${timecode}","${replyAuthor}","${replyDate}","${replyText}"\n`;
      });
    });
    
    return {
      content: csvContent,
      filename: `${cleanFilename(videoFile)}.csv`
    };
  } catch (err) {
    console.error('Error generating CSV file:', err);
    throw new Error('Failed to generate CSV file');
  }
};

/**
 * Sanitize text for CSV export
 * @param {String} text - Text to sanitize
 * @returns {String} - Sanitized text
 */
export const sanitizeForCSV = (text) => {
  return text.replace(/"/g, '""');
}

/**
 * Clean a file name for use in a file path
 * @param {Object} videoFile - Video file object
 * @returns {String} - Cleaned file name
 */
export const cleanFilename = (videoFile) => {
  const filename = videoFile.file_nickname || videoFile.file_name;
  return String(filename).replace(/\.[^/.]+$/, '');
}

/**
 * Download content as a file
 * @param {String} content - File content
 * @param {String} filename - Name of the file to download
 * @param {String} type - MIME type of the file
 */
export const downloadFile = (content, filename, type = 'text/plain') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download comments as an EDL file
 * @param {Array} comments - Array of comment objects
 * @param {Object} videoFile - Video file object
 */
export const downloadAsEDL = (comments, videoFile) => {
  const { content, filename } = generateEDL(comments, videoFile);
  downloadFile(content, filename, 'text/plain');
};

/**
 * Download comments as a CSV file
 * @param {Array} comments - Array of comment objects
 * @param {Object} videoFile - Video file object
 */
export const downloadAsCSV = (comments, videoFile) => {
  const { content, filename } = generateCSV(comments, videoFile);
  downloadFile(content, filename, 'text/csv;charset=utf-8;');
};
